import { useCallback, useState } from 'react';

/**
 * A Google Places "session" token (UUID v4) that groups every
 * autocomplete keystroke in one search flow with the closing Place
 * Details lookup. Google bills the whole session as one Place Details
 * call (autocomplete keystrokes become free) — but only when both
 * sides of the call carry the same token.
 *
 * Lifecycle: hold one token while the user is typing + picking. Call
 * `reset()` after a successful pick so the next search opens a fresh
 * session. Closing the modal / clearing the field is also a good
 * place to reset.
 */
export function usePlaceSearchSession(): {
  token: string;
  reset: () => void;
} {
  const [token, setToken] = useState<string>(() => makeToken());
  const reset = useCallback(() => setToken(makeToken()), []);
  return { token, reset };
}

/**
 * Mints a single UUID-shaped session token for callers that group one
 * search + its closing lookup outside the hook lifecycle (e.g. the
 * one-shot booking-extract resolver). Always UUID-shaped so it passes
 * the server's `z.string().uuid()` validation on `places.search` /
 * `places.lookup`.
 */
export function newPlaceSearchSessionToken(): string {
  return makeToken();
}

function makeToken(): string {
  // crypto.randomUUID is available in modern browsers and in Hermes
  // 0.74+ (RN 0.74 / Expo 50+). Fall back to a Math.random v4 shape
  // for any environment that's missing it — Google's session-token
  // check is opaque (any non-empty UUID-shaped string works), so the
  // fallback is fine even if a touch less random.
  const g = (globalThis as { crypto?: { randomUUID?: () => string } | undefined}).crypto;
  if (g?.randomUUID) return g.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
